import { MenuItem } from '@material-ui/core';
import { useStripe } from '@stripe/react-stripe-js';
import { CreateTokenBankAccountData, StripeError } from '@stripe/stripe-js';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { FormBodyAPI } from 'src/constants';
import { BaseTextField } from 'src/legacy/components/TextField';
import { BankFormModel } from 'src/store/payments/types';
import { alertSnackbar } from 'src/store/ui/actions';

import { RoutingIcon } from './RoutingIcon';

const validationScheme = Yup.object().shape({
  accountNumber: Yup.string().required('Account number is required.'),
  accountHolderName: Yup.string().required('Account holder name is required.'),
  accountHolderType: Yup.string().required('Account holder type is required.'),
  routingNumber: Yup.string().required('Routing number is required.'),
});

interface UseAddBankFormOptions {
  clientSecret?: string;
  enableMicrodepositVerification?: boolean;
}

export const useAddBankForm = (options?: UseAddBankFormOptions) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const clientSecret = options?.clientSecret;
  const enableMicrodepositVerification =
    options?.enableMicrodepositVerification;

  const stripe = useStripe();
  const getSubmitValues = async (
    values: Partial<BankFormModel> | undefined,
  ) => {
    if (!stripe || !values) {
      return { isValid: false };
    }
    let errorMessage;
    try {
      // If microdeposit verification is enabled and we have a clientSecret,
      // use SetupIntent flow with on_behalf_of - creates PaymentMethod on platform
      // on_behalf_of makes microdeposits show the portal's business name
      if (enableMicrodepositVerification && clientSecret) {
        const result = await stripe.confirmUsBankAccountSetup(clientSecret, {
          payment_method: {
            us_bank_account: {
              account_number: values.accountNumber || '',
              routing_number: values.routingNumber || '',
              account_holder_type: values.accountHolderType as
                | 'individual'
                | 'company',
            },
            billing_details: {
              name: values.accountHolderName,
            },
          },
        });

        if (result.error) {
          errorMessage = result.error.message;
        } else if (result.setupIntent) {
          // SetupIntent confirmed - PaymentMethod created on platform
          // Parent will attach it to the connected account's customer
          return { setupIntent: result.setupIntent };
        } else {
          errorMessage = 'Invalid bank information. Cannot be saved';
        }
      } else {
        // Old flow: Create a bank account token directly
        const result = await stripe.createToken('bank_account', {
          object: 'bank_account',
          country: 'US',
          currency: 'usd',
          account_number: values.accountNumber,
          account_holder_name: values.accountHolderName,
          account_holder_type: values.accountHolderType,
          routing_number: values.routingNumber,
        } as CreateTokenBankAccountData);

        if (result.error) {
          errorMessage = result.error.message;
        } else if (result.token) {
          return { token: result.token };
        } else {
          errorMessage = 'Invalid bank information. Cannot be saved';
        }
      }
    } catch (err) {
      const error = err as StripeError;
      errorMessage = error.message;
    }
    if (errorMessage) {
      dispatch(alertSnackbar({ errorMessage }));
    }
    return { isValid: false };
  };

  const FormRenderer: React.FC<FormBodyAPI> = ({
    values,
    touched,
    errors,
    handleBlur,
    handleChange,
  }) => {
    return (
      <>
        <div>
          <BaseTextField
            error={Boolean(
              touched.accountHolderName && errors.accountHolderName,
            )}
            fullWidth
            helperText={
              (touched.accountHolderName && errors.accountHolderName) || ' '
            }
            label="Account holder name"
            id="account-holder-name-input"
            name="accountHolderName"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.accountHolderName}
            variant="outlined"
            sizeVariant="medium"
          />
        </div>
        <div>
          <BaseTextField
            sizeVariant="medium"
            select
            fullWidth
            variant="outlined"
            name="accountHolderType"
            value={values.accountHolderType}
            onChange={handleChange}
            error={Boolean(
              touched.accountHolderType && errors.accountHolderType,
            )}
            helperText={
              (touched.accountHolderType && errors.accountHolderType) || ' '
            }
            label="Account holder type"
            onBlur={handleBlur}
          >
            <MenuItem value="individual">Individual</MenuItem>
            <MenuItem value="company">
              {t(($) => $.company, { count: 1 })}
            </MenuItem>
          </BaseTextField>
        </div>
        <RoutingIcon style={{ height: 84, width: 217 }} />
        <div>
          <BaseTextField
            error={Boolean(touched.routingNumber && errors.routingNumber)}
            fullWidth
            helperText={(touched.routingNumber && errors.routingNumber) || ' '}
            label="Routing number"
            id="routing-number-input"
            name="routingNumber"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.routingNumber}
            variant="outlined"
            sizeVariant="medium"
          />
        </div>
        <div>
          <BaseTextField
            error={Boolean(touched.accountNumber && errors.accountNumber)}
            fullWidth
            helperText={(touched.accountNumber && errors.accountNumber) || ' '}
            label="Account number"
            id="account-number-input"
            name="accountNumber"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.accountNumber}
            variant="outlined"
            sizeVariant="medium"
          />
        </div>
      </>
    );
  };

  return { validationScheme, getSubmitValues, FormRenderer };
};
